import { Input } from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { SearchIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import * as previews from './templates-previews';
import * as styles from './templates-gallery.css';
import { createTemplateDoc, type OrgTemplateKey } from './templates';

/**
 * Templates page content («Шаблоны») — a Notion-style template gallery over
 * the page builder (`createTemplateDoc` / `createCabinetDoc`). Every card is a
 * drawn paper blank of the page it creates; a click builds the doc and opens
 * it. The wedding suite mirrors the top Notion wedding templates (planning
 * hub, budget, RSVP, venue hunting, registry…), the business and personal
 * blocks mirror the top of the other categories.
 */

type CategoryKey =
  | 'all'
  | 'wedding'
  | 'people'
  | 'couple'
  | 'business'
  | 'personal';

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'all', label: 'Все шаблоны' },
  { key: 'wedding', label: 'Свадьба' },
  { key: 'people', label: 'Люди и роли' },
  { key: 'couple', label: 'Для пары' },
  { key: 'business', label: 'Бизнес' },
  { key: 'personal', label: 'Личное' },
];

// the warm palette of the paper covers — sage, blush, champagne, slate…
const SAGE = '#8FA07C';
const BLUSH = '#C4867E';
const CHAMPAGNE = '#C2A15C';
const SLATE = '#7E93A8';
const PLUM = '#9B8AA8';
const TERRACOTTA = '#C08457';

type GalleryEntry = {
  id: OrgTemplateKey | 'cabinet';
  title: string;
  description: string;
  category: Exclude<CategoryKey, 'all'>;
  accent: string;
  art: ReactNode;
};

const ENTRIES: GalleryEntry[] = [
  // ——— свадьба ———
  {
    id: 'hub',
    title: 'Свадьба: центр планирования',
    description:
      'Чек-лист по фазам, бюджет, гости и подрядчики — вся свадьба на одной странице',
    category: 'wedding',
    accent: PLUM,
    art: <previews.PreviewCabinet accent={PLUM} />,
  },
  {
    id: 'budget',
    title: 'Бюджет свадьбы',
    description: 'План, факт и оплата по каждой статье — в рублях',
    category: 'wedding',
    accent: CHAMPAGNE,
    art: <previews.PreviewTable accent={CHAMPAGNE} money />,
  },
  {
    id: 'timeline',
    title: 'Тайминг подготовки',
    description: '30 этапов от первой встречи до медового месяца',
    category: 'wedding',
    accent: SAGE,
    art: <previews.PreviewGantt accent={SAGE} />,
  },
  {
    id: 'day',
    title: 'Сценарий дня',
    description: 'Поминутный план свадебного дня — от сборов до фейерверка',
    category: 'wedding',
    accent: TERRACOTTA,
    art: <previews.PreviewGantt accent={TERRACOTTA} hour />,
  },
  {
    id: 'checklist',
    title: 'Чек-лист подготовки',
    description:
      'Дела со сроками и ответственными — отмечайте по мере готовности',
    category: 'wedding',
    accent: SAGE,
    art: <previews.PreviewChecklist accent={SAGE} />,
  },
  {
    id: 'bookings',
    title: 'Бронирования',
    description: 'Площадка, отель, трансфер: что забронировано и оплачено',
    category: 'wedding',
    accent: SLATE,
    art: <previews.PreviewTable accent={SLATE} chips />,
  },
  {
    id: 'venue',
    title: 'Подбор площадки',
    description: 'Сравнение площадок: цены, депозиты и вопросы на просмотре',
    category: 'wedding',
    accent: TERRACOTTA,
    art: <previews.PreviewTable accent={TERRACOTTA} chips />,
  },
  {
    id: 'honeymoon',
    title: 'Медовый месяц',
    description: 'Маршрут, бюджет и список сборов — шаг за шагом',
    category: 'wedding',
    accent: PLUM,
    art: <previews.PreviewGantt accent={PLUM} />,
  },
  // ——— люди и роли ———
  {
    id: 'guests',
    title: 'Гости и рассадка',
    description: 'Список гостей, ответы на приглашения и столы',
    category: 'people',
    accent: BLUSH,
    art: <previews.PreviewTable accent={BLUSH} chips />,
  },
  {
    id: 'seating',
    title: 'План столов',
    description: 'Кто где сидит: столы, вместимость и заметки',
    category: 'people',
    accent: CHAMPAGNE,
    art: <previews.PreviewTable accent={CHAMPAGNE} />,
  },
  {
    id: 'team',
    title: 'Команда',
    description: 'Кто ведёт свадьбу с вашей стороны: роли, контакты, ставки',
    category: 'people',
    accent: CHAMPAGNE,
    art: <previews.PreviewContacts accent={CHAMPAGNE} />,
  },
  {
    id: 'contacts',
    title: 'Подрядчики',
    description: 'Площадка, фото, музыка, декор — телефоны и цены',
    category: 'people',
    accent: SLATE,
    art: <previews.PreviewContacts accent={SLATE} />,
  },
  {
    id: 'crm',
    title: 'CRM: пары и заявки',
    description: 'Лиды, встречи и договоры — воронка вашей студии',
    category: 'people',
    accent: PLUM,
    art: <previews.PreviewTable accent={PLUM} chips />,
  },
  // ——— для пары ———
  {
    id: 'cabinet',
    title: 'Личный кабинет пары',
    description:
      'Страница для пары: о вас, задачи, смета и гости в одном месте',
    category: 'couple',
    accent: PLUM,
    art: <previews.PreviewCabinet accent={PLUM} />,
  },
  {
    id: 'brief',
    title: 'Бриф пары',
    description: 'Анкета первой встречи: о паре, стиль, бюджет и пожелания',
    category: 'couple',
    accent: BLUSH,
    art: <previews.PreviewDoc accent={BLUSH} />,
  },
  {
    id: 'website',
    title: 'Страница для гостей',
    description:
      'Приглашение со всей информацией: программа, трансфер, пожелания',
    category: 'couple',
    accent: PLUM,
    art: <previews.PreviewDoc accent={PLUM} />,
  },
  {
    id: 'registry',
    title: 'Список подарков',
    description: 'Что пара хочет получить — и что уже забронировано',
    category: 'couple',
    accent: BLUSH,
    art: <previews.PreviewChecklist accent={BLUSH} />,
  },
  // ——— бизнес ———
  {
    id: 'projects',
    title: 'Проекты и задачи',
    description: 'Статусы, приоритеты и сроки — как в таск-трекере',
    category: 'business',
    accent: SLATE,
    art: <previews.PreviewBoard accent={SLATE} />,
  },
  {
    id: 'content',
    title: 'Контент-план',
    description: 'Материалы по площадкам и форматам со статусами',
    category: 'business',
    accent: TERRACOTTA,
    art: <previews.PreviewTable accent={TERRACOTTA} chips />,
  },
  {
    id: 'meeting',
    title: 'Протокол встречи',
    description: 'Решения и договорённости — чтобы ничего не терялось',
    category: 'business',
    accent: SLATE,
    art: <previews.PreviewDoc accent={SLATE} />,
  },
  {
    id: 'finance',
    title: 'Финансы студии',
    description: 'Доходы и расходы с категориями и датами',
    category: 'business',
    accent: CHAMPAGNE,
    art: <previews.PreviewTable accent={CHAMPAGNE} money />,
  },
  {
    id: 'invoices',
    title: 'Счета и оплаты',
    description: 'Кто оплатил, кто должен — со статусами',
    category: 'business',
    accent: CHAMPAGNE,
    art: <previews.PreviewTable accent={CHAMPAGNE} money />,
  },
  {
    id: 'knowledge',
    title: 'База знаний студии',
    description: 'Регламенты и процессы — чтобы всё шло по одному сценарию',
    category: 'business',
    accent: PLUM,
    art: <previews.PreviewDoc accent={PLUM} />,
  },
  // ——— личное ———
  {
    id: 'goals',
    title: 'Цели и OKR',
    description: 'Большая цель и ключевые результаты с прогрессом',
    category: 'personal',
    accent: SAGE,
    art: <previews.PreviewGoals accent={SAGE} />,
  },
  {
    id: 'habits',
    title: 'Трекер привычек',
    description: 'Отмечайте по дням недели и следите за серией',
    category: 'personal',
    accent: SAGE,
    art: <previews.PreviewHabits accent={SAGE} />,
  },
  {
    id: 'reading',
    title: 'Читательский список',
    description: 'Книги, статусы, оценки и любимые цитаты',
    category: 'personal',
    accent: BLUSH,
    art: <previews.PreviewTable accent={BLUSH} chips />,
  },
  {
    id: 'meals',
    title: 'Меню на неделю',
    description: 'Меню на каждый день и готовый список покупок',
    category: 'personal',
    accent: TERRACOTTA,
    art: <previews.PreviewChecklist accent={TERRACOTTA} />,
  },
];

const FEATURED_ID: GalleryEntry['id'] = 'hub';

interface TemplatesGalleryContentProps {
  /** the «Личный кабинет» card delegates to the cabinet section picker */
  onRequestCabinet?: () => void;
}

export const TemplatesGalleryContent = ({
  onRequestCabinet,
}: TemplatesGalleryContentProps) => {
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const [category, setCategory] = useState<CategoryKey>('all');
  const [query, setQuery] = useState('');
  const [creatingId, setCreatingId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const map = new Map<CategoryKey, number>();
    for (const entry of ENTRIES) {
      map.set(entry.category, (map.get(entry.category) ?? 0) + 1);
      map.set('all', (map.get('all') ?? 0) + 1);
    }
    return map;
  }, []);

  const entries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ENTRIES.filter(
      entry =>
        (category === 'all' || entry.category === category) &&
        (!q || `${entry.title} ${entry.description}`.toLowerCase().includes(q))
    );
  }, [category, query]);

  const showFeatured = category === 'all' && !query.trim();
  const featuredEntry = showFeatured
    ? ENTRIES.find(entry => entry.id === FEATURED_ID)
    : undefined;
  const gridEntries = featuredEntry
    ? entries.filter(entry => entry !== featuredEntry)
    : entries;

  const useTemplate = useCallback(
    (entry: GalleryEntry) => {
      // the cabinet opens its section picker instead of creating a doc directly
      if (entry.id === 'cabinet') {
        onRequestCabinet?.();
        return;
      }
      setCreatingId(entry.id);
      createTemplateDoc(docsService, entry.id)
        .then(id => workbench.openDoc(id))
        .catch(console.error)
        .finally(() => setCreatingId(null));
    },
    [docsService, onRequestCabinet, workbench]
  );

  return (
    <div className={styles.root}>
      <nav className={styles.rail}>
        <div className={styles.railHeading}>Категории</div>
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            className={clsx(
              styles.railItem,
              category === cat.key && styles.railItemActive
            )}
            onClick={() => setCategory(cat.key)}
          >
            <span>{cat.label}</span>
            <span className={styles.railCount}>{counts.get(cat.key) ?? 0}</span>
          </button>
        ))}
      </nav>

      <div className={styles.main}>
        <div className={styles.hero}>
          <h1 className={styles.heroTitle}>Шаблоны</h1>
          <p className={styles.heroSubtitle}>
            Готовые страницы для организации свадеб и работы студии — выбирайте
            нужную и заполняйте под конкретную пару.
          </p>
          <div className={styles.searchRow}>
            <Input
              preFix={<SearchIcon width={16} height={16} />}
              placeholder="Найти шаблон…"
              value={query}
              onChange={setQuery}
            />
          </div>
        </div>

        {featuredEntry ? (
          <div className={styles.featured}>
            <div className={styles.featuredArt}>{featuredEntry.art}</div>
            <div>
              <div className={styles.featuredTag}>Начните с этого</div>
              <h2 className={styles.featuredTitle}>{featuredEntry.title}</h2>
              <p className={styles.featuredDesc}>{featuredEntry.description}</p>
              <button
                className={styles.featuredAction}
                disabled={creatingId !== null}
                onClick={() => useTemplate(featuredEntry)}
              >
                {creatingId === featuredEntry.id
                  ? 'Создаём…'
                  : 'Использовать шаблон →'}
              </button>
            </div>
          </div>
        ) : null}

        <div className={styles.grid}>
          {gridEntries.map(entry => (
            <button
              key={entry.id}
              className={styles.card}
              style={
                {
                  '--card-accent': entry.accent,
                } as CSSProperties
              }
              disabled={creatingId !== null}
              onClick={() => useTemplate(entry)}
            >
              <div className={styles.cardArt}>{entry.art}</div>
              <div className={styles.cardTitle}>{entry.title}</div>
              <div className={styles.cardDesc}>{entry.description}</div>
              <div className={styles.cardAction}>
                {creatingId === entry.id ? 'Создаём…' : 'Использовать шаблон'}
              </div>
            </button>
          ))}
          {gridEntries.length === 0 ? (
            <div className={styles.empty}>
              Ничего не нашлось. Попробуйте другой запрос — или выберите «Все
              шаблоны».
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
